import React, { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config.js";

import GlobalLoadingOverlay from "./GlobalLoadingOverlay";
import { useAppDispatch } from "../hooks";
import authAction from "../actions/authAction";
import App from "../App";
import {
  clearWebAuthTokenProvider,
  removeWebAuthTokenMirror,
  setWebAuthTokenMirror,
  setWebAuthTokenProvider,
} from "../utils/authTokenStorage";

const AUTH0_SCOPE = "openid profile email offline_access";
const REFRESH_TOKEN_RECOVERY_KEY = "auth0_refresh_recovery_attempted";

const isMissingRefreshTokenError = (err: any): boolean => {
  const errorCode = err?.error || err?.code;
  const message = typeof err?.message === "string" ? err.message : "";

  return (
    errorCode === "missing_refresh_token" ||
    message.includes("Missing Refresh Token")
  );
};

const AuthorizedApp: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
    user,
    error
  } = useAuth0();
  const dispatch = useAppDispatch();
  const [accessGranted, setAccessGranted] = useState<boolean | null>(null);
  const [authorizationError, setAuthorizationError] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [hasHandledRedirect, setHasHandledRedirect] = useState(false);
  const lastProvidedTokenRef = useRef<string | null>(null);

  // Allow selected public routes without authentication
  const isPublicRoute =
    location.pathname === '/password' ||
    location.pathname === '/ninjaone/oauth/callback' ||
    location.pathname === '/m365/oauth/callback' ||
    location.pathname === '/help-center/release-notes';
  // Allow /embed routes to handle their own authentication (they'll show login UI)
  const isEmbedRoute = location.pathname.startsWith('/embed');

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const provider = async (options?: { forceRefresh?: boolean }) => {
      const tokenResult = await getAccessTokenSilently({
        authorizationParams: {
          audience: config.audience || '',
        },
        cacheMode: options?.forceRefresh ? 'off' : 'on',
      } as any);
      const token =
        typeof tokenResult === "string"
          ? tokenResult
          : tokenResult?.access_token || "";

      if (token && token !== lastProvidedTokenRef.current) {
        lastProvidedTokenRef.current = token;
        setWebAuthTokenMirror(token);
        dispatch(authAction.setAccessToken(token));
        if (user) {
          dispatch(authAction.setUserData(user));
        }
      }
      return token;
    };

    setWebAuthTokenProvider(provider);
    return () => clearWebAuthTokenProvider(provider);
  }, [dispatch, getAccessTokenSilently, isAuthenticated, user]);

    useEffect(() => {
    const checkAccess = async () => {
      // Allow public routes (like /password) without authentication
      if (isPublicRoute) {
        setAccessGranted(true);
        return;
      }
      
      // Allow embed routes to handle their own authentication
      if (isEmbedRoute) {
        setAccessGranted(true);
        return;
      }

      // Skip if already processing
      if (isCheckingAuth) {
        return;
      }

      // Skip if already granted
      if (accessGranted === true) {
        return;
      }
      
      // Handle Auth0 errors
      if (error) {
        setAuthorizationError(`Authentication error: ${error.message}`);
        setAccessGranted(false);
        return;
      }

      // Wait for Auth0 to finish loading
      if (isLoading) {
        return;
      }

      // Redirect to login if not authenticated
      // Preserve the current route so we can redirect back after authentication
      if (!isAuthenticated) {
        const params = new URLSearchParams(location.search);
        const isSignup = params.get('screen_hint') === 'signup';
        const loginHint = params.get('login_hint')?.trim();
        // Invitation hints belong to Auth0, not the page restored after signup.
        if (isSignup) {
          params.delete('screen_hint');
          params.delete('login_hint');
        }
        const remainingSearch = params.toString();
        const search = isSignup ? (remainingSearch ? `?${remainingSearch}` : '') : location.search;
        const returnTo = location.pathname + search + location.hash;
        // Store the return URL in sessionStorage as a fallback
        // Auth0 SDK's appState should also work, but this ensures we can recover it
        if (returnTo !== '/') {
          sessionStorage.setItem('auth0_return_to', returnTo);
        }
        await loginWithRedirect({
          ...(isSignup && {
            authorizationParams: {
              screen_hint: 'signup',
              ...(loginHint && { login_hint: loginHint }),
            },
          }),
          appState: {
            returnTo: returnTo
          }
        });
        return;
      }

      // Wait for user data
      if (!user?.email) {
        // Set a timeout to avoid infinite waiting
        setTimeout(() => {
          if (!user?.email) {
            setAccessGranted(false);
            setAuthorizationError("Failed to get user information from Auth0");
          }
        }, 5000);
        return;
      }

      setIsCheckingAuth(true);

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: config.audience || '',
          },
        });

        sessionStorage.removeItem(REFRESH_TOKEN_RECOVERY_KEY);
        lastProvidedTokenRef.current = token;
        setWebAuthTokenMirror(token);
        dispatch(authAction.setAccessToken(token));
        dispatch(authAction.setUserData(user));

        // Check authorization with backend (identity from JWT, not query param)
        const baseURL = config.apiUrl || '';
        const response = await axios.get(`${baseURL}/auth/check`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        });
        
        if (response.data.authorized) {
          setAccessGranted(true);
          setAuthorizationError(null);
          
          // Check if we have a return URL stored from before the OAuth redirect
          // This handles the case where user refreshes a page and gets redirected to login
          // Only do this once to avoid infinite redirects
          if (!hasHandledRedirect) {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');
            const state = urlParams.get('state');
            
            // If we have OAuth callback parameters, check for stored returnTo path
            if (code && state) {
              const returnTo = sessionStorage.getItem('auth0_return_to');
              if (returnTo && returnTo !== '/' && returnTo !== location.pathname + location.search + location.hash) {
                // Mark as handled and clear the stored returnTo
                setHasHandledRedirect(true);
                sessionStorage.removeItem('auth0_return_to');
                // Navigate to the original page
                // Use replace to avoid adding to history
                navigate(returnTo, { replace: true });
              } else {
                // No returnTo or already on that path, just clear it
                sessionStorage.removeItem('auth0_return_to');
                setHasHandledRedirect(true);
              }
            } else {
              // No OAuth callback params, clear any stale returnTo
              sessionStorage.removeItem('auth0_return_to');
              setHasHandledRedirect(true);
            }
          }
        } else {
          setAccessGranted(false);
          setAuthorizationError(response.data.error || 'Not authorized');
        }
          
      } catch (err: any) {
        if (isMissingRefreshTokenError(err)) {
          const recoveryAttempted = sessionStorage.getItem(REFRESH_TOKEN_RECOVERY_KEY) === "1";

          if (!recoveryAttempted) {
            sessionStorage.setItem(REFRESH_TOKEN_RECOVERY_KEY, "1");

            const returnTo = location.pathname + location.search + location.hash;
            if (returnTo !== '/') {
              sessionStorage.setItem('auth0_return_to', returnTo);
            }

            await loginWithRedirect({
              appState: { returnTo },
              authorizationParams: {
                audience: config.audience || '',
                scope: AUTH0_SCOPE,
                prompt: 'login',
              },
            });
            return;
          }
        } else {
          sessionStorage.removeItem(REFRESH_TOKEN_RECOVERY_KEY);
        }

        setAccessGranted(false);

        if (err.response?.status === 403) {
          setAuthorizationError(`You are not authorized to access this application. User: ${user.email}`);
        } else if (err.code === 'ECONNREFUSED') {
          setAuthorizationError("Unable to connect to server. Please try again later.");
        } else {
          setAuthorizationError(`Authorization error: ${err.message}`);
        }
      } finally {
        setIsCheckingAuth(false);
      }
    };

    // Run the check
    checkAccess();
    // isEmbedRoute must be listed: embed vs main app switches without Auth0 loading flags changing.
  }, [isLoading, isAuthenticated, user?.email, accessGranted, error, isPublicRoute, isEmbedRoute]);

  if (isPublicRoute) {
    return <App />;
  }

  // Show loading spinner while checking authorization. Pre-shell: there is no
  // nav to leave to yet, so this one legitimately covers the viewport.
  if (isLoading || accessGranted === null) {
    return <GlobalLoadingOverlay variant="app" />;
  }

  // Show access denied page (NOTHING else)
  if (!accessGranted) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#f8fafc',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px'
        }}>
          <h1 style={{ 
            color: '#dc2626', 
            marginBottom: '20px',
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            Access Denied
          </h1>
          <p style={{ 
            color: '#374151', 
            marginBottom: '20px', 
            lineHeight: 1.6,
            fontSize: '16px'
          }}>
            {authorizationError || "You are not authorized to access this application."}
          </p>
          <p style={{ 
            color: '#6b7280', 
            fontSize: '14px',
            marginBottom: '30px'
          }}>
            If you believe this is an error, please contact your system administrator.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Try Again
            </button>
            <button 
              onClick={() => {
                removeWebAuthTokenMirror();
                logout({
                  logoutParams: {
                    returnTo: window.location.origin,
                  },
                });
              }} 
              style={{
                padding: '12px 24px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Logout & Try Different Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is authorized - render the full application
  return <App />;
};

export default AuthorizedApp;
